import React, { useCallback, useEffect, useLayoutEffect, useRef , useState } from 'react';
import { useAsyncValue, useSubmit, useNavigation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Sentry from '@sentry/react';

// Redux
import { roidOTSelectedReset } from '../../../../state/actions/ui/roidOTSelectedGeneralView';
import { tabs as tabsAction } from '../../../../state/actions/ui/tabs';
import i18n from '../../../../i18n';

// Components
import Tabs from '../../../../components/display/Tabs';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
import OperarisTabContent, {
  buildOperatorsInitialData,
} from '../components/OperarisTabContent';
import UbicacioTab from '../components/UbicacioTab';
import { deriveCrew } from '../../../../components/forms/ActionForms/OperatorsAndVehicles/crew';

// Redux
import { tabsType } from '../../../../state/reducers/ui/tabs';
import { getCurrentIndexTabFormByTab } from '../../../../state/selectors/ui/tabs';

// Types
import { NewRouteLoaderData } from '../types';
import { OptionSelected } from '../../../../components/inputs/SearchableDropdown/types';
import {
  OperatorsSelectionType,
  VehiclesSelectionType,
} from '../../../../components/display/tables/formTables/OperatorsSelectionTable/types';
import type { CrewSummary } from '../../../../components/forms/ActionForms/OperatorsAndVehicles/crew';

// Utils
import { pkFormatter, parsePk } from '../../../../utils/transform';
import { showErrorToast } from '../../../../utils/toasts';
import { computeCrewErrors } from '../../../../utils/surveillanceRoute';
import { client } from '../../../../gql/client';
import { GET_ROAD_BY_ID } from '../../../../gql/queries/geoPositions/roads';
import { presetToFormRoadRanges } from './presetToForm';

/**
 * Valor sintético de la opción «Otro nombre» del selector de plantillas: revela
 * el campo de texto de siempre para un recorrido no previsto (refuerzo,
 * extraordinario) sin obligar a pasar antes por Gestión.
 */
const OTHER_NAME_OPTION = '__other__';
import { decompressSessionStorage } from '../../../../utils/sessionStorage';
import { removeNewSurveillanceRouteStorage } from '../../../../utils/resetData';
import { SR_OPERATORS_FORM_KEY } from './saveData';

// Schema
import {
  newRouteSchema,
  type NewRouteFormValues,
  type RouteRoadRangeFormValue,
} from './schema';

// Map base (mismo componente que el wizard de actuación)
import { useTenantDefaultSurveillanceTeam } from '../../../../hooks/useTenantDefaultSurveillanceTeam';
import { useTenantMapCenter } from '../../../../hooks/useTenantMapCenter';

// Styles
import { PageContainer, ButtonsContainer } from './styles';

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
//
// Task 3 (2026-07-07, refactor de patrones RV): la tab "Operaris" y la tab
// "Ubicació" viven ahora en `components/OperarisTabContent` y
// `components/UbicacioTab` — esta página solo orquesta el form (react-hook-
// form), la cascada de carretera (`GET_ROAD_BY_ID`, es orquestación de
// página) y el submit. `computeCrewErrors` es función pura en
// `utils/surveillanceRoute.ts`; `buildOperatorsInitialData` vive junto a
// `OperarisTabContent` (el componente que consume su output).
// ═══════════════════════════════════════════════════════════════════

/** Valores de Ubicació precargados en modo edición (F4 2026-07-17). Mismos
 *  campos que el defaultValues del create + `teamId` del RV. */
export interface EditRouteInitialValues extends NewRouteFormValues {
  teamId: string;
}

/** Semilla del borrador de Operarios en modo edición: la tripulación actual
 *  del RV (operarios + parejas vehículo→conductor) en el shape que consume
 *  el OperatorsAndVehicleForm / deriveCrew. */
export interface EditRouteOperatorsSeed {
  operatorsTable: OperatorsSelectionType[];
  vehicleTable: Record<string, VehiclesSelectionType>;
}

/** Payload que el wizard pasa a `onEditSubmit` tras validar (yup + FR-3.4 +
 *  equipo obligatorio). El diff contra `dataToCompare` + el submit a la action
 *  viven en la página EditRoute (patrón guard-en-página de EditCampaign). */
export interface EditRouteSubmitPayload {
  name: string;
  /** D2 (2026-07-20): descripción del recorrido (opcional), diffeada por la
   *  página EditRoute contra `dataToCompare.description`. */
  description: string;
  concessionId: string;
  /** Multi-carretera (2026-08-15): las carreteras del recorrido y el rango de
   *  cada una. `pkInit`/`pkEnd` siguen siendo los de la PRIMERA, que es lo que
   *  el backend guarda en el recorrido. */
  roadIds: string[];
  roadRanges: RouteRoadRangeFormValue[];
  pkInit: string;
  pkEnd: string;
  teamId: string;
  crew: CrewSummary;
}

export interface NewRoutePageProps {
  /** Modo edición (F4 2026-07-17): reusa el wizard con valores precargados,
   *  selector de equipo SIEMPRE visible y submit vía `onEditSubmit` (diff). */
  editMode?: boolean;
  editInitialValues?: EditRouteInitialValues;
  editOperatorsSeed?: EditRouteOperatorsSeed;
  /** Key sessionStorage del borrador de Operarios (EDIT en edición para no
   *  pisar el de creación). */
  operatorsFormKey?: string;
  /** El RV tiene tramos registrados → mecánica del aviso al cambiar carretera. */
  hasSegments?: boolean;
  /** Carreteras originales del RV — para detectar el cambio (aviso §2.5). */
  originalRoadIds?: string[];
  /** Handler de guardado en edición (diff + submit lo hace la página). */
  onEditSubmit?: (payload: EditRouteSubmitPayload) => void;
}

export default function NewRoutePage({
  editMode = false,
  editInitialValues,
  editOperatorsSeed,
  operatorsFormKey,
  hasSegments = false,
  originalRoadIds = [],
  onEditSubmit,
}: NewRoutePageProps = {}): JSX.Element {
  const { t } = useTranslation(['pages', 'common', 'buttons']);
  // Centro/zoom por defecto del mapa: config por tenant con fallback Cataluña.
  const defaultMap = useTenantMapCenter();
  // Feature `surveillance_default_team` (2026-07-17): equipo de vigilancia
  // por defecto del tenant. Con config (caso UTE → ÈXSER): preseleccionado y
  // SIN selector. Sin config: selector visible (equipos isSurveillance).
  const tenantDefaultTeamId = useTenantDefaultSurveillanceTeam();
  // En edición el equipo arranca preseleccionado con el del RV; en creación
  // vacío (lo resuelve el flag / la selección).
  const [selectedTeamId, setSelectedTeamId] = useState(
    editMode ? editInitialValues?.teamId ?? '' : '',
  );
  const dispatch = useDispatch();
  const loaderData = useAsyncValue() as NewRouteLoaderData;
  // Tour P5 (2026-07-07): la creación vive en la `action` del router
  // (patrón pass-data/save-data — ver ./action.ts y ./saveData.ts), no en
  // un hook: el componente valida y hace submit; el router crea y redirige.
  const submit = useSubmit();
  const navigation = useNavigation();
  const isCreating = navigation.state === 'submitting';

  // Key del borrador de Operarios: EDIT en edición (no pisa el de creación),
  // NEW en creación. La leen tanto la rehidratación (OperarisTabContent) como
  // el submit (deriveCrew abajo).
  const effectiveOperatorsKey = operatorsFormKey ?? SR_OPERATORS_FORM_KEY;

  // ═══════════════════════════════════════════════════════════════════
  // El asistente EMPIEZA EN BLANCO (incidente 2026-08-08)
  // ═══════════════════════════════════════════════════════════════════
  // El borrador de Operarios solo existe para sobrevivir al cambio de pestaña
  // Operaris↔Ubicació DENTRO de este mismo asistente. Sobrevivía también a
  // los intentos abandonados y, como la tripulación se deriva del borrador y
  // no de lo que la pantalla enseña, el siguiente recorrido nacía con la gente
  // y los coches del anterior — invisibles si el filtro de equipo ocultaba sus
  // filas. En la app nativa el WebView no se cierra nunca, así que el borrador
  // duraba días y se acumulaba. Mismo patrón que ya usan Actuación y Accidente
  // (`removeNewActionStorage`/`removeNewAccidentStorage` al entrar al wizard).
  //
  // 🔴 POR QUÉ AQUÍ Y NO EN OTRO SITIO — no lo muevas:
  //  - En un `useEffect`/`useLayoutEffect` LLEGA TARDE: `OperarisTabContent`
  //    lee el borrador en un `useMemo` durante SU render, y los hijos
  //    renderizan antes de que corran los efectos del padre → el formulario ya
  //    se habría inicializado con la tripulación fantasma (y `useCacheForm` la
  //    volvería a persistir).
  //  - En el LOADER se dispara de más: React Router revalida los loaders tras
  //    cada `action`, así que un guardado fallido borraría la tripulación que
  //    el usuario acaba de marcar.
  // Un inicializador perezoso corre UNA vez, en el primer render y antes que
  // los hijos. Purgar dos veces es inofensivo (StrictMode).
  //
  // ⚠️ SOLO en creación. En edición NO se purga: `EditRoutePage` ya siembra el
  // borrador de forma síncrona al montar (su `seededRef`, con la tripulación
  // real del recorrido que trae el loader) y esa siembra SOBRESCRIBE lo que
  // hubiera — el arrastre entre recorridos ahí ya estaba resuelto. Como el
  // padre siembra antes de rendernos, purgar aquí borraría justo esa semilla y
  // la edición perdería la tripulación.
  useState(() => {
    if (!editMode) removeNewSurveillanceRouteStorage('new');
    return true;
  });

  // V4 iter7+ (2026-05-07) — Issue A fix: limpiamos `roidOTSelected` al entrar
  // al wizard de nuevo RV. Sin esto, el breadcrumb mostraría el ROID del
  // último SR visitado (p.ej. `Su.0000251.0000.02`) en lugar de un breadcrumb
  // limpio "Recorrido de vigilancia / Nuevo recorrido".
  // useLayoutEffect (2026-07-20): con useEffect el reset corría DESPUÉS del
  // primer pintado — si redux traía la tab Ubicació de una visita anterior
  // (p.ej. entrar a editar desde la card de Órdenes asociadas tras haber
  // guardado desde Ubicació), se veía Ubicació un instante y saltaba a
  // Operarios. Pre-pintado = ambas entradas aterrizan SIEMPRE en la tab 0.
  useLayoutEffect(() => {
    dispatch(roidOTSelectedReset());
    // Tour P5: la tab activa vive en redux (permite saltar a Operarios al
    // fallar FR-3.4). Reset a la tab 0 al entrar para conservar el
    // comportamiento previo (defaultIndex estático 0).
    dispatch(tabsAction(tabsType.SURVEILLANCE_ROUTE, 0));
  }, [dispatch]);

  // Tab activa sincronizada con redux — Tabs re-sincroniza selectedIndex
  // cuando defaultIndex cambia (FIX BUG #35) y despacha al hacer click, así
  // que redux es la fuente única y no hay bucles.
  const activeTabIndex = useSelector(
    getCurrentIndexTabFormByTab(tabsType.SURVEILLANCE_ROUTE),
  );


  // Equipo por defecto del tenant: solo cuenta si existe entre los equipos
  // isSurveillance del loader (config apuntando a un equipo borrado/ajeno =
  // misconfiguración → se cae al selector, con warn del hook/parseo).
  const surveillanceTeams = loaderData?.teams || [];
  const effectiveDefaultTeamId =
    tenantDefaultTeamId &&
    surveillanceTeams.some((tm: any) => tm.id === tenantDefaultTeamId)
      ? tenantDefaultTeamId
      : null;
  // Equipo resuelto SIN ambigüedad: el del flag del tenant, o el ÚNICO equipo
  // de vigilancia que hay. En los dos casos el desplegable no ofrece ninguna
  // elección — solo puede volver a poner lo que ya está puesto.
  //
  // El equipo único ya lo resolvía la cadena de prioridades al guardar
  // (FR-3.2, paso 4); lo que faltaba era que el selector lo reflejara.
  const singleSurveillanceTeamId =
    surveillanceTeams.length === 1 ? surveillanceTeams[0].id : null;
  const resolvedTeamId = effectiveDefaultTeamId ?? singleSurveillanceTeamId;

  // 2026-08-17 (decisión del usuario): si el equipo se resuelve solo, el
  // selector se oculta TAMBIÉN al editar. Antes se mostraba siempre en edición
  // («editar es corregir», spec §3.1), pero con un solo equipo de vigilancia
  // no hay nada que corregir: el desplegable pedía elegir lo mismo que ya
  // estaba. Al ocultarlo no se pierde el equipo — en edición `selectedTeamId`
  // nace con el del recorrido y es el primero de la cadena de prioridades.
  //
  // 🔴 La única excepción es un recorrido cuyo equipo NO sea el resuelto (uno
  // antiguo cuyo equipo dejó de ser de vigilancia). Ahí sí se enseña, porque
  // esconderlo dejaría ese recorrido con un equipo que nadie puede ver ni
  // arreglar desde la pantalla.
  const editingWithForeignTeam =
    editMode &&
    !!editInitialValues?.teamId &&
    editInitialValues.teamId !== resolvedTeamId;
  const showTeamSelector = !resolvedTeamId || editingWithForeignTeam;
  const teamOptions = surveillanceTeams.map((tm: any) => ({
    label: tm.name,
    value: tm.id,
  }));

  // Form (replaces 9 useState calls)
  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<NewRouteFormValues>({
    // En edición precargamos la Ubicació del RV (incluidos los límites de vía
    // `pkInitRoad`/`pkEndRoad`, para que `pkValidation` del schema pase sin
    // re-disparar la cascada de carretera). En creación, vacío.
    defaultValues:
      editMode && editInitialValues
        ? {
            name: editInitialValues.name,
            description: editInitialValues.description ?? '',
            concessionId: editInitialValues.concessionId,
            roadIds: editInitialValues.roadIds,
            roadRanges: editInitialValues.roadRanges,
          }
        : {
            name: '',
            description: '',
            concessionId: '',
            roadIds: [],
            roadRanges: [],
          },
    resolver: yupResolver(newRouteSchema) as any,
  });

  // Watched values for cascade logic and button state
  const name = watch('name');
  const concessionId = watch('concessionId');
  // Multi-carretera (2026-08-15): antes era un `roadId` escalar con un par de
  // PK sueltos. Ahora hay una fila por carretera, y los extremos de cada vía
  // viven DENTRO de su fila — así `pkValidation` compara cada PK contra los
  // extremos de SU carretera y no contra los de la primera.
  const roadIds = watch('roadIds');
  const roadRanges = watch('roadRanges');

  // Tour P5: fallos FR-3.4 (operarios/vehículos/conductor) mostrados como
  // frases en rojo DENTRO de la tab Operarios — sin modal (feedback usuario).
  // Los errores GLOBALES de guardado NO se pintan inline: van por toast
  // desde el catch de ./action.ts (patrón de la app — ver useSurveillanceRoutes).
  const [operatorsErrors, setOperatorsErrors] = React.useState<string[]>([]);

  // Recalculo EN VIVO armado tras el primer Iniciar — espejo del
  // reValidateMode:'onChange' de react-hook-form (Ubicación solo re-valida
  // tras el primer submit; la tripulación hace lo mismo).
  const crewValidationArmedRef = useRef(false);

  // Cada cambio en la tab Operarios (via onCrewChange del form) recalcula
  // las frases — con bail de igualdad para no provocar bucles de render.
  // `computeCrewErrors` es un import estable (función pura en utils), así
  // que no entra en el array de dependencias.
  const handleCrewChange = useCallback(
    (crew: {
      operators: number;
      vehicles: number;
      vehiclesWithoutDriver: number;
    }) => {
      if (!crewValidationArmedRef.current) return;
      const errs = computeCrewErrors(crew);
      setOperatorsErrors((prev) => {
        if (
          prev.length === errs.length &&
          prev.every((msg, i) => msg === errs[i])
        ) {
          return prev;
        }
        return errs;
      });
    },
    [],
  );

  // Equipo OBLIGATORIO (2026-07-17 — el backend ahora rechaza
  // createSurveillanceRoute sin teamId): cuando el selector es visible y ni
  // la selección explícita, ni el borrador de Operarios, ni el flag, ni el
  // fallback de equipo único resuelven equipo, Iniciar se bloquea con error
  // inline junto al selector (misma mecánica que los PKs: mensaje
  // `validation:required` bajo el input). Armed = espejo de
  // crewValidationArmedRef: tras el primer fallo se re-valida EN VIVO al
  // tocar el selector.
  const [teamRequiredError, setTeamRequiredError] = useState(false);
  const teamValidationArmedRef = useRef(false);

  const handleTeamChange = useCallback((opt: OptionSelected | undefined) => {
    const value = opt?.value || '';
    setSelectedTeamId(value);
    if (teamValidationArmedRef.current) {
      setTeamRequiredError(!value);
    }
  }, []);

  // Ref for road dropdown imperative clear
  const roadDropdownRef = useRef<any>(null);

  const concessions = loaderData?.concessions || [];
  const allRoads = loaderData?.roads || [];

  /**
   * Plantillas de recorrido ACTIVAS del tenant (2026-08-16).
   *
   * 🔴 Vacío = el tenant no usa plantillas y el campo «Nombre del recorrido»
   * sigue siendo el `InputText` de siempre. Sin regresión ni migración para
   * quien no use esto; vaciar el catálogo es además el rollback.
   *
   * Solo se ofrecen las que caen en la concesión seleccionada: la plantilla no
   * guarda concesión, se deduce de sus carreteras.
   */
  const allPresets = loaderData?.presets || [];

  /**
   * ¿Se está escribiendo un nombre libre en vez de elegir plantilla?
   *
   * Arranca en `true` al EDITAR un recorrido cuyo nombre no sale de ninguna
   * plantilla: si no, la pantalla de edición ocultaría el nombre que el
   * recorrido ya tiene.
   */
  const [presetSelection, setPresetSelection] = useState<string>(() => {
    const current = editMode ? editInitialValues?.name ?? '' : '';
    if (!current) return '';
    // Al EDITAR, el desplegable arranca mostrando la plantilla de la que salió
    // el nombre; si no salió de ninguna, «Otro nombre» con el texto a la vista.
    const desdePlantilla = (loaderData?.presets || []).find(
      (preset) => preset.name === current
    );
    return desdePlantilla ? desdePlantilla.id : OTHER_NAME_OPTION;
  });
  const usingFreeName = presetSelection === OTHER_NAME_OPTION;

  // Filter roads by selected concession (cascade pattern from LocationForm)
  const filteredRoads = concessionId
    ? allRoads.filter((r: any) => r.concessionIDs?.includes(concessionId))
    : [];

  const concessionOptions = concessions.map((c: any) => ({
    label: c.name,
    value: c.id,
  }));

  const roadOptions = filteredRoads.map((r: any) => ({
    label: r.name,
    value: r.id,
  }));

  const presetOptions = React.useMemo(() => {
    const visibleRoadIds = new Set(filteredRoads.map((r: any) => r.id));
    return allPresets
      .filter(
        (preset) =>
          preset.roadIDs.length > 0 &&
          preset.roadIDs.every((roadId) => visibleRoadIds.has(roadId))
      )
      .map((preset) => ({ label: preset.name, value: preset.id }));
  }, [allPresets, filteredRoads]);

  /**
   * Al elegir plantilla se siembran nombre, carreteras y filas de PK de una
   * vez. Sin ir a la red: los extremos de cada vía salen del catálogo que el
   * loader ya trae, que es lo que permite hacerlo sin cobertura.
   *
   * Los PK quedan EDITABLES (decisión de producto): obra, desvío o corte
   * obligan a ajustar sobre la marcha.
   */
  const handlePresetChange = useCallback(
    (presetId: string) => {
      if (presetId === OTHER_NAME_OPTION) {
        setPresetSelection(OTHER_NAME_OPTION);
        setValue('name', '', { shouldValidate: true });
        return;
      }

      const preset = allPresets.find((item) => item.id === presetId);
      if (!preset) return;

      // 🔴 El desplegable tiene que quedarse enseñando la plantilla elegida.
      // Con el valor fijo a '' volvía al placeholder en cuanto se elegía: los
      // campos se rellenaban y el operario no podía ver de cuál venían.
      setPresetSelection(preset.id);
      setValue('name', preset.name, { shouldValidate: true });
      setValue('roadIds', [...preset.roadIDs], { shouldValidate: true });
      setValue('roadRanges', presetToFormRoadRanges(preset, allRoads), {
        shouldValidate: true,
      });
    },
    [allPresets, allRoads, setValue]
  );

  // Tour P4 (2026-07-06) — regla canónica de LocationForm: si solo hay una
  // concesión (caso UTE) se auto-selecciona y el dropdown NO se muestra
  // (misma regla que los filtros de tabla en P1).
  const singleConcession = concessionOptions.length === 1;
  const singleConcessionId = singleConcession ? concessionOptions[0].value : '';
  React.useEffect(() => {
    if (singleConcessionId && !concessionId) {
      setValue('concessionId', singleConcessionId);
    }
  }, [singleConcessionId, concessionId, setValue]);

  // Sentido removed from wizard — now asked in StartTimerModal per segment

  // Cascade: concession change -> clear roads + their PK rows
  const handleConcessionChange = useCallback(
    (opt: any) => {
      setValue('concessionId', opt?.value || '');
      setValue('roadIds', []);
      setValue('roadRanges', []);
      roadDropdownRef.current?.clearSelected?.();
    },
    [setValue],
  );

  /**
   * Selección de carreteras → una fila de PK por cada una, prerrellenada con
   * sus extremos (mismo gesto que el `onSelectRoad` del LocationForm, pero N
   * veces).
   *
   * Las filas que ya estaban se CONSERVAN tal cual: si el operario ya ajustó
   * el PK de la troncal y luego añade el ramal, no se le puede tirar lo que
   * escribió. Solo se piden los extremos de las carreteras nuevas.
   */
  const handleRoadsChange = useCallback(
    async (selectedIds: string[]) => {
      setValue('roadIds', selectedIds, { shouldValidate: true });

      const previous = getValues('roadRanges') ?? [];
      const byRoadId = new Map(previous.map((r) => [r.roadId, r]));

      try {
        const rows = await Promise.all(
          selectedIds.map(async (roadId) => {
            const kept = byRoadId.get(roadId);
            if (kept) return kept;

            const {
              data: { road },
            } = await client.query({
              query: GET_ROAD_BY_ID,
              variables: { id: roadId },
            });
            const boundsInit = pkFormatter(road.pkInitKm, road.pkInitMeter);
            const boundsEnd = pkFormatter(road.pkEndKm, road.pkEndMeter);
            return {
              roadId,
              pkInitRoad: boundsInit,
              pkEndRoad: boundsEnd,
              // Prerrellenados con los extremos; el operario los ajusta.
              pkInit: boundsInit,
              pkEnd: boundsEnd,
            };
          }),
        );
        // shouldValidate: si ya intentó Iniciar con un PK fuera de rango,
        // cambiar de carretera re-valida y limpia el error en vivo.
        setValue('roadRanges', rows, { shouldValidate: true });
      } catch (err) {
        Sentry.captureException(err);
        console.error('Error fetching road:', err);
        showErrorToast(i18n.t('toasts:error.generic'));
      }
    },
    [setValue, getValues],
  );

  // "Iniciar" — creates the entity and redirects to workspace
  const onSubmit = useCallback(
    async (data: NewRouteFormValues) => {
      // Inalcanzable en la práctica (yup ya validó que hay al menos una fila y
      // que sus PK tienen formato y rango); si ocurre es inconsistencia real →
      // mismo canal que los errores de la action: toast global.
      const everyRowParses =
        data.roadRanges.length > 0 &&
        data.roadRanges.every((r) => !!parsePk(r.pkInit) && !!parsePk(r.pkEnd));
      if (!everyRowParses) {
        showErrorToast(i18n.t('toasts:error.generic'));
        return;
      }

      // Tripulación desde el borrador persistido — derivada con deriveCrew
      // (crew.ts), la MISMA lógica que alimenta el recálculo en vivo. La
      // fuente de los vehículos es vehicleTable (vehículo→conductor);
      // vehiclesTable puede traer objetos vacíos {} y no es fiable.
      let crew = deriveCrew({});
      let teamId: string | undefined;
      try {
        const raw = sessionStorage.getItem(effectiveOperatorsKey);
        if (raw) {
          const decompressed = decompressSessionStorage(raw);
          const stored = JSON.parse(decompressed);
          // Cinturón (incidente 2026-08-08): el borrador puede arrastrar ids
          // que el catálogo ya no tiene. Fail-open si el catálogo viene vacío
          // — sin cobertura el operario tiene que poder crear el recorrido.
          //
          // ⚠️ SOLO al CREAR. En edición el borrador NO es un resto de nada:
          // lo siembra `EditRoutePage` con la tripulación REAL del recorrido
          // que trae su loader, y ese loader conserva a propósito parejas cuyo
          // vehículo no resuelve en el catálogo (borrado, o de un uso que el RV
          // ya no lista) — es su "red de seguridad" declarada. Reconciliar ahí
          // tiraría justo lo que él se molesta en salvar: editar el nombre de un
          // recorrido le quitaría un vehículo sin decir nada.
          crew = deriveCrew(
            stored,
            editMode
              ? undefined
              : {
                  operatorIds: (
                    loaderData?.internalOperators?.operators || []
                  ).map((op: any) => op.id),
                  vehicleIds: (
                    loaderData?.internalOperators?.vehicles || []
                  ).map((v: any) => v.id),
                },
          );

          const firstOpTeamName = (stored?.operatorsTable || []).find(
            Boolean,
          )?.team;
          if (firstOpTeamName) {
            const matched = (loaderData?.teams || []).find(
              (t: any) => t.name === firstOpTeamName,
            );
            teamId = matched?.id;
          }
        }
      } catch (e) {
        console.warn('[NewRoute] unable to read operators session:', e);
      }

      // Prioridad del equipo del RV (feature surveillance_default_team):
      // 1) selección EXPLÍCITA del selector (visible solo sin config);
      // 2) equipo inferido del borrador de Operarios (comportamiento previo);
      // 3) equipo por defecto del tenant (config — caso UTE → ÈXSER);
      // 4) fallback V4 FR-3.2: único equipo isSurveillance. Sin equipo el
      //    Tablet no puede operar su timer (validateTimerOwnership).
      if (selectedTeamId) {
        teamId = selectedTeamId;
      }
      if (!teamId && effectiveDefaultTeamId) {
        teamId = effectiveDefaultTeamId;
      }
      if (!teamId && (loaderData?.teams || []).length === 1) {
        teamId = loaderData!.teams[0].id;
      }

      // Equipo OBLIGATORIO (2026-07-17): el backend rechaza
      // createSurveillanceRoute sin teamId ('Debes seleccionar un equipo
      // para el recorrido'). Si tras la cadena de prioridades sigue sin
      // resolverse (solo posible con el selector visible), bloqueamos el
      // submit con error inline junto al selector y saltamos a la tab
      // Ubicació para que el usuario lo VEA (mismo patrón de salto que los
      // errores de Operarios — Tabs re-sincroniza con defaultIndex, BUG #35).
      if (!teamId) {
        teamValidationArmedRef.current = true;
        setTeamRequiredError(true);
        dispatch(tabsAction(tabsType.SURVEILLANCE_ROUTE, 1));
        return;
      }
      setTeamRequiredError(false);

      // ── Tour P5 (2026-07-07) — FR-3.4: validaciones bloqueantes de la tab
      // Operarios ANTES de crear. Mismos mensajes que el validador final del
      // wizard de actuaciones (utils/formValidation.ts): mínimo 1 operario,
      // mínimo 1 vehículo y conductor asignado en todo vehículo
      // seleccionado. Feedback usuario: SIN modal — los fallos se marcan en
      // la propia tab con frases en rojo, se salta a ella vía redux, y a
      // partir del primer Iniciar se recalculan EN VIVO (onCrewChange).
      crewValidationArmedRef.current = true;
      const validationErrors = computeCrewErrors(crew);

      setOperatorsErrors(validationErrors);
      if (validationErrors.length > 0) {
        // Salta a la tab Operarios para que el usuario VEA las frases en
        // rojo (Tabs re-sincroniza selectedIndex con defaultIndex — BUG #35).
        dispatch(tabsAction(tabsType.SURVEILLANCE_ROUTE, 0));
        return;
      }

      // EDICIÓN (F4 2026-07-17): mismas validaciones que crear (yup + FR-3.4 +
      // equipo obligatorio), pero el guardado NO crea: delega en la página
      // EditRoute, que hace el diff contra `dataToCompare` (guard sin-cambios
      // en la página, patrón EditCampaign) y hace el submit a su action.
      if (editMode) {
        onEditSubmit?.({
          name: data.name.trim(),
          description: (data.description ?? '').trim(),
          concessionId: data.concessionId,
          roadIds: data.roadIds,
          roadRanges: data.roadRanges,
          // Los PK del recorrido son los de su PRIMERA carretera: el backend
          // los sigue guardando aparte y todo lo que ya los lee (filtros,
          // columnas, informes) sigue funcionando igual.
          pkInit: data.roadRanges[0].pkInit,
          pkEnd: data.roadRanges[0].pkEnd,
          teamId,
          crew,
        });
        return;
      }

      // Tour P5 (2026-07-07) — patrón pass-data/save-data de la app: el
      // componente valida (yup + FR-3.4, arriba) y SOLO hace submit; la
      // creación (mutation + limpieza del borrador + redirect al detalle)
      // vive en la `action` del router (./action.ts → ./saveData.ts), como
      // en NewSurveillanceRouteOrder/Summary y el wizard de work orders.
      const formData = new FormData();
      formData.set('intent', 'save-data');
      formData.set('name', data.name.trim());
      formData.set('description', (data.description ?? '').trim());
      formData.set('concessionId', data.concessionId);
      // Multi-carretera: la lista y el rango de cada una. Los `pkInit`/`pkEnd`
      // sueltos siguen siendo los de la PRIMERA carretera, que es lo que el
      // backend guarda en el recorrido.
      formData.set('roadIds', JSON.stringify(data.roadIds));
      formData.set('roadRanges', JSON.stringify(data.roadRanges));
      formData.set('pkInit', data.roadRanges[0].pkInit);
      formData.set('pkEnd', data.roadRanges[0].pkEnd);
      if (teamId) formData.set('teamId', teamId);
      // 🔴 La tripulación viaja YA DERIVADA y validada (incidente 2026-08-08).
      // Antes `saveData` volvía a leer el borrador crudo de sessionStorage por
      // su cuenta: se validaba una cosa y se mandaba otra — cualquier resto de
      // un intento anterior entraba en el recorrido sin pasar por aquí. Una
      // sola derivación, y es la que el usuario acaba de ver en pantalla.
      formData.set(
        'crew',
        JSON.stringify({
          operatorIds: crew.operatorIds,
          assignments: crew.assignments,
        }),
      );
      submit(formData, { method: 'POST', action: '.' });
    },
    [
      loaderData,
      selectedTeamId,
      effectiveDefaultTeamId,
      effectiveOperatorsKey,
      submit,
      dispatch,
      editMode,
      onEditSubmit,
    ],
  );

  const handleStart = handleSubmit(onSubmit);

  // V4 FR-3.1 (audit Disc#8, 2026-05-28): el StatusPanel derecho con
  // Fecha/Hora inicio-fin + botones Iniciar/Finalitzar se ELIMINÓ. El timer
  // es el TimerOverlay del topbar (D3); las fechas/horas las marcan los
  // tramos, no el form de creación. El botón que crea el RV vive ahora en
  // el `ButtonsContainer` inferior (FR-3.1 "botón Continuar abajo derecha").

  // Botón abajo derecha habilitado solo si la ubicación está completa.
  const isFormValid =
    !!name?.trim() &&
    !!concessionId &&
    (roadIds?.length ?? 0) > 0 &&
    // Cada carretera elegida tiene que traer su fila de PK completa: con
    // varias, que una esté a medias es tan inválido como que falte la única.
    (roadRanges?.length ?? 0) === (roadIds?.length ?? 0) &&
    (roadRanges ?? []).every((r) => !!r.pkInit && !!r.pkEnd);

  // En edición, la card de Operarios arranca con la tripulación actual del RV
  // (semilla del loader). En creación, tabla vacía (comportamiento previo).
  const baseInitialData = editMode && editOperatorsSeed
    ? {
        ...buildOperatorsInitialData(loaderData),
        operatorsTable: editOperatorsSeed.operatorsTable,
        vehicleTable: editOperatorsSeed.vehicleTable,
      }
    : buildOperatorsInitialData(loaderData);

  // Aviso §2.5: el RV tiene tramos y se cambió la carretera.
  //
  // Multi-carretera (2026-08-15): pasa de comparar dos ids a comparar dos
  // CONJUNTOS. Avisa igual si se quita una carretera que si se añade — en los
  // dos casos hay tramos ya registrados que pueden quedar apuntando a una
  // carretera que el recorrido ya no cubre.
  const roadChangeWarning =
    editMode &&
    hasSegments &&
    (originalRoadIds?.length ?? 0) > 0 &&
    (roadIds?.length ?? 0) > 0 &&
    (originalRoadIds.length !== roadIds.length ||
      originalRoadIds.some((id) => !roadIds.includes(id)));

  const dataTab = [
    {
      label: t('pages:surveillance.operatorsTab'),
      content: (
        <OperarisTabContent
          baseInitialData={baseInitialData}
          operatorsErrors={operatorsErrors}
          onCrewChange={handleCrewChange}
          operatorsFormKey={effectiveOperatorsKey}
          // Addendum RV 2026-07-20: solo en edición se muestra el selector
          // activos/inactivos/todos (patrón Actuaciones/validación) — un
          // operario/vehículo inactivo ya asignado debe verse. Crear RV
          // (editMode=false) lo deja como Actuaciones: gated solo por URL.
          showCrewStateFilter={editMode}
        />
      ),
    },
    {
      label: t('pages:surveillance.locationTab'),
      content: (
        <UbicacioTab
          register={register}
          setValue={setValue}
          control={control}
          errors={errors}
          singleConcession={singleConcession}
          concessionOptions={concessionOptions}
          roadOptions={roadOptions}
          presetOptions={presetOptions}
          handlePresetChange={handlePresetChange}
          usingFreeName={usingFreeName}
          presetSelection={presetSelection}
          handleConcessionChange={handleConcessionChange}
          handleRoadsChange={handleRoadsChange}
          roadDropdownRef={roadDropdownRef}
          roadIds={roadIds}
          roadRanges={roadRanges}
          defaultMap={defaultMap}
          showTeamSelector={showTeamSelector}
          teamOptions={teamOptions}
          selectedTeamId={selectedTeamId}
          handleTeamChange={handleTeamChange}
          teamError={
            // Misma key/mecánica que los campos requeridos del schema yup
            // (schema.ts usa i18n.t('validation:required') idéntico).
            teamRequiredError ? i18n.t('validation:required') : undefined
          }
          roadChangeWarning={roadChangeWarning}
        />
      ),
    },
  ];

  return (
    <PageContainer>
      {/* Fix 2026-07-04 (feedback usuario): la tab por defecto es
          "Operarios y vehículos" (índice 0), como en el mockup del spec.
          Tour P5: defaultIndex viene de redux (reset a 0 al montar) para
          que el ValidationErrorsModal pueda saltar a la tab Operarios. */}
      <Tabs
        data={dataTab}
        typeTab={tabsType.SURVEILLANCE_ROUTE}
        defaultIndex={activeTabIndex}
      />
      {/* V4 FR-3.1: botón único de creación abajo derecha (sustituye al
          StatusPanel timer + al botón "Iniciar tramo!" disabled). */}
      <ButtonsContainer>
        <MainButton
          text={
            // Edición: "Desar" (clave existente); creación: "Iniciar".
            // El asistente CREA el recorrido; arrancarlo es otro gesto, con su
            // propio botón en la ficha. Decir «Iniciar» aquí prometía algo que
            // este botón no hace (2026-08-19, petición del usuario).
            editMode ? t('buttons:save') : t('buttons:create')
          }
          onClick={handleStart}
          disabled={isCreating || !isFormValid}
        />
      </ButtonsContainer>
      {/* Aquí había un `<Loader/>` propio para `isCreating`. Se retira porque
          desde 2026-08-14 el layout superpone el velo de navegación en TODA
          navegación —y un envío de formulario lo enciende al instante—, así que
          se veían DOS: el de esta página apagado por detrás del velo y el del
          velo delante. `isCreating` se conserva para deshabilitar el botón.
          Spec: docs/superpowers/specs/2026-08-14-feedback-navegacion-asistentes-design.md */}
    </PageContainer>
  );
}
